import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, X, Map, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { scrollToSection } from "@/lib/scroll";
import { priceForZone } from "@/lib/data";

const ZONES = [
  { id: "vipexp", label: "VIP Experience", price: 1000 },
  { id: "selectp", label: "Select+ · Meet & Greet", price: 149 },
  { id: "select", label: "Select", price: 129 },
  { id: "general", label: "General", price: 90 },
];

const QuickPick = () => {
  const [open, setOpen] = useState(false);
  const [zone, setZone] = useState("general");
  const [count, setCount] = useState(2);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState({});
  const { toggleSeat, clear } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    apiGet("/api/hype")
      .then((d) => {
        const m = {};
        (d.zones || []).forEach((z) => {
          m[z.id] = z.available;
        });
        setLeft(m);
      })
      .catch(() => {});
  }, [open]);

  // Park the floating button while the ticket area (#seating) is on screen.
  const [parked, setParked] = useState(false);
  useEffect(() => {
    const target = document.getElementById("seating");
    if (!target) return;
    const obs = new IntersectionObserver(
      ([entry]) => setParked(entry.isIntersecting),
      { threshold: 0, rootMargin: "-90px 0px 0px 0px" }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, []);

  const go = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await apiPost("/api/quick-pick", { zone, count });
      clear();
      res.seats.forEach((s) =>
        toggleSeat({
          id: s.seat_id,
          zone: s.zone,
          zoneLabel: s.zone_label,
          row: s.row,
          n: s.number,
          price: s.price,
          reg: priceForZone(s.zone) || s.price,
        })
      );
      const saved = res.seats.reduce((s, x) => s + Math.max((priceForZone(x.zone) || x.price) - x.price, 0), 0);
      toast.success(`${res.seats.length} best seat${res.seats.length > 1 ? "s" : ""} held — $${res.total}`);
      navigate("/checkout");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't find seats — try the full map.");
      setBusy(false);
    }
  };

  const active = ZONES.find((z) => z.id === zone);
  const estPrice = active.price;

  return (
    <>
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: parked && !open ? 0 : 1,
          y: parked && !open ? 120 : 0,
        }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => setOpen(!open)}
        data-testid="quickpick-open-btn"
        className={`fixed bottom-5 right-5 z-50 btn-gold rounded-full pl-5 pr-6 py-3.5 text-sm font-bold flex items-center gap-2 shadow-2xl ${
          parked && !open ? "pointer-events-none" : ""
        }`}
      >
        <Zap size={16} className="fill-[#0B1021]" />
        {open ? "Close Fast Pick" : "Fast Pick — Best Seats"}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="quickpick-panel"
            className="fixed bottom-20 right-5 z-50 w-[calc(100vw-40px)] max-w-sm glass rounded-3xl p-6 border-[#F3D068]/30 shadow-[0_30px_90px_rgba(0,0,0,0.7)]"
          >
            <div className="flex items-center justify-between">
              <p className="eyebrow">Fast Pick Funnel</p>
              <button onClick={() => setOpen(false)} className="text-[#8A99B5] hover:text-[#F3D068]" aria-label="Close" data-testid="quickpick-close-btn">
                <X size={18} />
              </button>
            </div>
            <h3 className="font-display text-2xl text-[#FDFBF7] mt-3 leading-snug">
              The easiest way in the door
            </h3>
            <p className="text-xs text-[#8A99B5] mt-2 leading-relaxed">
              Tell us your zone and party size — we'll instantly hold the best available seats,
              closest to the stage and center. No map needed.
            </p>

            <p className="text-[10px] uppercase tracking-[0.22em] text-[#8A99B5] mt-6 mb-2.5">
              1 · Your experience
            </p>
            <div className="flex flex-wrap gap-2">
              {ZONES.map((z) => (
                <button
                  key={z.id}
                  onClick={() => setZone(z.id)}
                  data-testid={`quickpick-zone-${z.id}`}
                  className={`rounded-full px-4 py-2 text-xs font-medium border transition-all ${
                    zone === z.id
                      ? "bg-[#D4AF37] text-[#0B1021] border-[#D4AF37] font-bold"
                      : "border-white/15 text-[#C5CEE0] hover:border-[#D4AF37]/50"
                  }`}
                >
                  {z.label} · ${z.price}
                  {left[z.id] !== undefined && (
                    <span className={left[z.id] <= 100 ? "text-[#F59E0B] font-semibold" : "opacity-70"}>
                      {" "}· {left[z.id]} left
                    </span>
                  )}
                </button>
              ))}
            </div>

            <p className="text-[10px] uppercase tracking-[0.22em] text-[#8A99B5] mt-6 mb-2.5">
              2 · Party size
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCount(Math.max(1, count - 1))}
                data-testid="quickpick-count-minus"
                className="w-10 h-10 rounded-full border border-white/15 text-[#F3D068] text-lg hover:border-[#D4AF37]/60 transition-colors"
              >
                −
              </button>
              <span className="font-display text-3xl text-[#FDFBF7] w-8 text-center" data-testid="quickpick-count-value">
                {count}
              </span>
              <button
                onClick={() => setCount(Math.min(8, count + 1))}
                data-testid="quickpick-count-plus"
                className="w-10 h-10 rounded-full border border-white/15 text-[#F3D068] text-lg hover:border-[#D4AF37]/60 transition-colors"
              >
                +
              </button>
              <span className="text-xs text-[#8A99B5] ml-auto">
                est. <span className="text-[#F3D068] font-bold text-base">${estPrice * count}</span>{" "}
              </span>
            </div>

            <button
              onClick={go}
              disabled={busy}
              data-testid="quickpick-submit-btn"
              className="btn-gold w-full rounded-full py-4 text-sm font-bold mt-7 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Holding your seats…
                </>
              ) : (
                <>
                  <Zap size={15} className="fill-[#0B1021]" /> Find My Best Seats
                </>
              )}
            </button>
            <button
              onClick={() => { setOpen(false); scrollToSection("seat-map"); }}
              data-testid="quickpick-map-link"
              className="w-full text-center text-xs text-[#8A99B5] hover:text-[#F3D068] transition-colors mt-4 inline-flex items-center justify-center gap-1.5"
            >
              <Map size={12} /> or choose every seat yourself on the full map
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuickPick;
